import type { WelcomeCategory, WelcomeSuggestionSource } from '@server/welcome-suggestions';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BarChart3, ListChecks, type LucideIcon, Search, Users } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/_shared/components/ui/button';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';
import { isSetupOnHome, useSetupProgress } from '@/_shared/hooks/useSetupProgress';
import { ANALYTICS_EVENTS, trackClientEvent } from '@/_shared/lib/analytics';
import { SETUP_STEP_TOTAL } from '@/_shared/lib/setup-steps';
import { cn } from '@/_shared/lib/utils';
import { SetupPanel, TRY_AUDIENCE_PROMPT } from '@/setup/components/SetupPanel';
import { useSetupPanelVisible } from '@/setup/hooks/useSetupPanelVisible';

import { ChatWelcomeHeader } from '../components/ChatWelcomeHeader';
import { MessageInput } from '../components/MessageInput';
import { ConnectWarehousePrompt } from '../components/onboarding/ConnectWarehousePrompt';
import { NoWarehouseHome } from '../components/onboarding/NoWarehouseHome';
import { useWarehouseGate } from '../hooks/useWarehouseGate';
import { type PendingTurnRouterState, sendPromptCopy } from '../lib/ai-turn-events';
import { extractErrorMessage } from '../lib/extract-error';

interface Category {
  id: WelcomeCategory['id'];
  label: string;
  icon: LucideIcon;
  examples: { label: string; prompt: string }[];
}

const CATEGORY_HEADLINE: Record<string, string> = {
  Explore: 'Explore data',
  Visualise: 'Visualise insights',
  'Build Audiences': 'Build audiences',
};

const CATEGORY_ICONS: Record<WelcomeCategory['id'], LucideIcon> = {
  explore: Search,
  visualise: BarChart3,
  audiences: Users,
};

function withIcons(categories: WelcomeCategory[]): Category[] {
  return categories.map((cat) => ({ ...cat, icon: CATEGORY_ICONS[cat.id] }));
}

/** Which set the chips came from, for the suggestion events. `none` renders no chips, so never reaches one. */
function eventSource(source: WelcomeSuggestionSource | undefined): { source?: 'model' | 'fixed' | 'setup' } {
  return source === undefined || source === 'none' ? {} : { source };
}

export function ChatWelcome(): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { slug, wp, workspace, isSetupPanelEnabled } = useWorkspace();
  const orpc = useOrpc();

  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  // The chips an open tab shows. A refetch that lands while it is open waits for the next open, so
  // the list never changes under the person reading it.
  const [openCategories, setOpenCategories] = useState<Category[] | null>(null);
  // Which set the open snapshot came from, so the events describe the chips on screen.
  const [openSource, setOpenSource] = useState<WelcomeSuggestionSource | undefined>(undefined);
  const examplesRef = useRef<HTMLDivElement>(null);
  const [sending, setSending] = useState(false);
  const [prefillValue, setPrefillValue] = useState<string | undefined>(undefined);
  const [prefillNonce, setPrefillNonce] = useState(0);
  const [prefillAnnouncement, setPrefillAnnouncement] = useState('');
  // The example last inserted into the composer, so the send can say whether it was that example, edited
  // or not. Neither text is sent to analytics.
  const [insertedExample, setInsertedExample] = useState<{
    label: string;
    prompt: string;
    position: number;
    category: Category['id'];
    source: WelcomeSuggestionSource | undefined;
  } | null>(null);

  // ACT2-1108: chat opens once a warehouse is connected. Only a loaded, empty list closes it: while the
  // list loads or after it fails, the composer shows as before, so an unknown never blocks anyone.
  // Only with the setup panel on (ACT2-1108 flag): off, the home reads no account list, as before.
  const { data: accountsData, isSuccess: accountsLoaded } = useQuery({
    ...orpc.settings.warehouses.listAccounts.queryOptions(),
    enabled: isSetupPanelEnabled,
  });
  const noWarehouse = isSetupPanelEnabled && accountsLoaded && accountsData.accounts.length === 0;
  const setup = useSetupProgress({ poll: true });
  const showSetupPanel = useSetupPanelVisible(setup);
  // ACT2-1108: while the setup panel is up, this page is the workspace's setup page.
  usePageTitle(isSetupOnHome(setup) ? 'Setup' : 'New chat');

  const { data: skillsData } = useQuery(orpc.skills.listInvokable.queryOptions({ input: {} }));
  const hasInvokableSkills = (skillsData?.skills ?? []).some((skill) => skill.enabled);

  const { data: suggestionsData } = useQuery({
    ...orpc.chat.welcomeSuggestions.queryOptions({ input: {} }),
    retry: false,
    // A fresh read each time New chat opens, so a deleted item's chip is gone; none while it is open,
    // so chips never change on screen. The read is one stored row, not a model call.
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  // A tab with no chips would open onto nothing, so only tabs that have chips are shown. With the
  // flag off, before the first stored set, or after a failed run, there are none and no row renders.
  const liveCategories = withIcons((suggestionsData?.categories ?? []).filter((cat) => cat.examples.length > 0));
  const categories = selectedCategory && openCategories ? openCategories : liveCategories;
  const liveSource = suggestionsData?.source;
  const source = selectedCategory && openCategories ? openSource : liveSource;

  const createMutation = useMutation(
    orpc.chat.create.mutationOptions({
      onSuccess: (data) => {
        void queryClient.invalidateQueries({
          queryKey: orpc.chat.list.queryOptions({ input: {} }).queryKey,
        });
        return data;
      },
    }),
  );

  const sendMessageMutation = useMutation(
    orpc.chat.sendMessage.mutationOptions({
      onSuccess: (_, variables) => {
        void queryClient.invalidateQueries({
          queryKey: orpc.chat.list.queryOptions({ input: {} }).queryKey,
        });
        void queryClient.invalidateQueries({
          queryKey: orpc.chat.get.queryOptions({ input: { id: variables.id } }).queryKey,
        });
      },
    }),
  );

  // Best-effort orphan cleanup: when create succeeds but sendMessage fails, we'd
  // otherwise leave an empty session in the sidebar with no obvious origin.
  const cleanupOrphanMutation = useMutation(
    orpc.chat.delete.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: orpc.chat.list.queryOptions({ input: {} }).queryKey,
        });
      },
    }),
  );

  const handleSend = useCallback(
    async (content: string, invokedSkillIds?: string[]): Promise<void> => {
      // Welcome → first message: persist the user_message BEFORE navigating, so
      // the new ChatConversation's chat.get returns the row instead of racing
      // sendMessage and stamping an empty sseMessages into local state.
      // sendMessage is a single DB write — the backend fires the agent turn
      // without awaiting it.
      const submittedAt = performance.now();
      setSending(true);
      try {
        const data = await createMutation.mutateAsync({});
        let turnId: string | undefined;
        try {
          const variables = {
            id: data.session.id,
            content,
            invokedSkillIds: invokedSkillIds ?? [],
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            clientRequestId: crypto.randomUUID(),
          };
          const result = await sendMessageMutation.mutateAsync(variables);
          turnId = result.turnId;
          const inserted = insertedExample;
          setInsertedExample(null);
          // Still the example only while the message starts with its button text, which is always the
          // first words of the inserted prompt. Cleared and retyped, it is the user's own message.
          const text = content.trim();
          const fromExample = inserted !== null && text.startsWith(inserted.label.trim());
          // The chat view this navigates to sends the answer copy (router state below).
          if (turnId) {
            sendPromptCopy(turnId, data.session.id, fromExample ? 'starter_suggestion' : 'composer', content.length, {
              accountId: workspace.id,
              feature: 'web_chat',
              surface: 'web',
            });
          }
          if (fromExample) {
            trackClientEvent(ANALYTICS_EVENTS.chatSuggestionSent, {
              surface: 'welcome',
              category: inserted.category,
              position: inserted.position,
              edited: text !== inserted.prompt.trim(),
              sessionId: data.session.id,
              turnId,
              ...eventSource(inserted.source),
            });
          }
        } catch (err: unknown) {
          cleanupOrphanMutation.mutate({ id: data.session.id });
          toast.error(extractErrorMessage(err));
          return;
        }
        // Pass turnId via router state so ChatConversation can open the SSE
        // stream immediately and filter the turn from chat.get data.
        const state: PendingTurnRouterState | undefined = turnId
          ? { pendingTurnId: turnId, aiTurn: { submittedAt, timeOrigin: performance.timeOrigin } }
          : undefined;
        void navigate(wp(`/chat/${data.session.id}`), { state });
      } catch (err: unknown) {
        toast.error(extractErrorMessage(err));
      } finally {
        setSending(false);
      }
    },
    [navigate, wp, workspace.id, createMutation, sendMessageMutation, cleanupOrphanMutation, insertedExample],
  );

  function insertExample(cat: Category, index: number, example: Category['examples'][number]): void {
    trackClientEvent(ANALYTICS_EVENTS.chatSuggestionClicked, {
      surface: 'welcome',
      category: cat.id,
      position: index + 1,
      suggestionCount: cat.examples.length,
      ...eventSource(source),
    });
    setInsertedExample({ label: example.label, prompt: example.prompt, position: index + 1, category: cat.id, source });
    setSelectedCategory(null);
    setPrefillValue(example.prompt);
    setPrefillNonce((n) => n + 1);
    setPrefillAnnouncement(`Example inserted in the composer: ${example.label}`);
  }

  const restoreToComposer = (content: string): void => {
    setPrefillValue(content);
    setPrefillNonce((n) => n + 1);
  };
  const gate = useWarehouseGate(handleSend, { restore: restoreToComposer });

  // ACT2-1108: the setup panel's "Try it" — the prompt goes in the composer, focused with the cursor at
  // the end, for the person to edit and send.
  const tryAudience = (): void => {
    setInsertedExample(null);
    setPrefillValue(TRY_AUDIENCE_PROMPT);
    setPrefillNonce((n) => n + 1);
    setPrefillAnnouncement(`Inserted in the composer: ${TRY_AUDIENCE_PROMPT}`);
  };

  // On a short phone the last chip lands below the fold; bring the opened list into view.
  useEffect(() => {
    if (!selectedCategory) return;
    examplesRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [selectedCategory]);

  // ACT2-1108: the way back to a hidden panel on this page too, not only in the sidebar.
  const showContinueSetup = setup.status !== null && !setup.complete && setup.prefs.hidden;
  // The button unmounts on click: move focus to the panel it brings back, so a keyboard or screen-reader
  // user lands there instead of on the page body.
  const setupPanelRef = useRef<HTMLElement>(null);
  const focusSetupPanelNext = useRef(false);
  useEffect(() => {
    if (!focusSetupPanelNext.current || !showSetupPanel) return;
    focusSetupPanelNext.current = false;
    setupPanelRef.current?.focus();
  }, [showSetupPanel]);
  const continueSetup = (): void => {
    // The status is not polled while the panel is hidden: read it again, since another admin may have
    // finished setup meanwhile.
    void queryClient.invalidateQueries({ queryKey: orpc.setup.status.queryOptions().queryKey });
    focusSetupPanelNext.current = true;
    setup.prefs.setHidden(false);
  };

  const composer = (
    <MessageInput
      // Setup panel off: the home holds a question behind the connect prompt, as before (ACT2-690).
      onSend={isSetupPanelEnabled ? handleSend : gate.gatedSend}
      disabled={sending}
      variant="hero"
      workspaceSlug={slug}
      hasInvokableSkills={hasInvokableSkills}
      prefillValue={prefillValue}
      prefillNonce={prefillNonce}
      prefillReplace
    />
  );
  // Always-mounted SR live region so staging is announced when it appears (a region inserted together with its
  // content is announced inconsistently). Mirrors MultiSelectList.
  const liveRegion = (
    <span className="sr-only" aria-live="polite">
      {prefillAnnouncement}
    </span>
  );
  const header = (
    <div className="mb-8">
      <ChatWelcomeHeader
        pinnedTitle={selectedCategory ? (CATEGORY_HEADLINE[selectedCategory] ?? null) : null}
        titles={categories.flatMap((cat) => CATEGORY_HEADLINE[cat.label] ?? [])}
      />
    </div>
  );
  const suggestions = (
    <>
      {/* Wraps instead of scrolling: on a narrow phone a scrolled row hides a tab with no cue that it
          is there. */}
      {categories.length > 0 ? (
        <div className="mt-5 py-0.5">
          <div className="flex flex-wrap justify-center gap-1 sm:gap-5">
            {categories.map((cat) => (
              <button
                key={cat.label}
                aria-pressed={selectedCategory === cat.label}
                onClick={(e) => {
                  e.stopPropagation();
                  if (selectedCategory !== cat.label) {
                    // `cat` is the list this tab shows: the open snapshot, or the live list it is about
                    // to snapshot, so the count is what the person sees.
                    trackClientEvent(ANALYTICS_EVENTS.chatSuggestionShown, {
                      surface: 'welcome',
                      category: cat.id,
                      suggestionCount: cat.examples.length,
                      ...eventSource(source),
                    });
                  }
                  if (!selectedCategory) {
                    setOpenCategories(liveCategories);
                    setOpenSource(liveSource);
                  }
                  setSelectedCategory(cat.label);
                }}
                className={cn(
                  'flex h-9 shrink-0 items-center gap-1 rounded-full border px-2 py-1 text-sm transition-colors sm:h-7',
                  selectedCategory === cat.label
                    ? 'border-primary bg-primary/10 font-medium text-primary'
                    : 'border-border bg-card text-foreground hover:border-primary hover:bg-muted',
                )}
              >
                <cat.icon size={16} className={selectedCategory === cat.label ? undefined : 'text-primary'} />
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {selectedCategory
        ? (() => {
            const cat = categories.find((c) => c.label === selectedCategory);
            if (!cat) return null;
            return (
              <div
                ref={examplesRef}
                className="mt-3 flex w-full animate-slide-up flex-col"
                role="presentation"
                onClick={(e) => e.stopPropagation()}
              >
                {cat.examples.map((ex, i) => (
                  <button
                    key={i}
                    onClick={() => insertExample(cat, i, ex)}
                    className={cn(
                      'flex min-h-10 gap-2.5 px-2 py-2 text-left text-sm text-foreground transition-colors hover:bg-muted',
                      i < cat.examples.length - 1 && 'border-b border-border',
                    )}
                  >
                    <cat.icon size={16} className="mt-0.5 shrink-0 text-muted-foreground" />
                    <span className="line-clamp-2">{ex.label}</span>
                  </button>
                ))}
              </div>
            );
          })()
        : null}
    </>
  );

  // ACT2-1108: with the setup panel off (`enable-lookout-setup-panel`), the home is as it was: the composer
  // always shows, and a question asked with no warehouse waits behind the connect prompt.
  if (!isSetupPanelEnabled) {
    if (gate.held !== null) {
      const { content } = gate.held;
      // Success navigates to the new chat and unmounts this view. On failure handleSend has already
      // toasted, so the question goes back in the composer rather than being lost.
      const restoreOnFailure = (sent: Promise<void>): void => void sent.finally(() => restoreToComposer(content));
      return (
        // Same frame as ChatConversation: the prompt docks where the composer sits in a chat.
        <div className="flex min-h-0 flex-1 flex-col justify-end">
          <div className="relative mx-auto w-full max-w-4xl shrink-0 px-6 py-4">
            <ConnectWarehousePrompt
              held={gate.held}
              onConnected={() => restoreOnFailure(gate.release())}
              onSendAnyway={() => restoreOnFailure(gate.sendAnyway())}
              onDismiss={gate.dismiss}
            />
          </div>
        </div>
      );
    }
    return (
      <div
        className="flex min-h-0 flex-1 flex-col items-center overflow-y-auto px-4 pt-10 pb-10"
        role="presentation"
        onClick={() => selectedCategory && setSelectedCategory(null)}
      >
        <div className="w-full max-w-[680px] animate-scale-in md:mt-[20vh]">
          {header}
          {composer}
          {liveRegion}
          {suggestions}
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-0 flex-1 flex-col overflow-y-auto lg:flex-row lg:overflow-hidden">
      {showContinueSetup && (
        <div className="absolute top-3 right-4 z-10 p-0.5">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            Icon={<ListChecks />}
            label={`Continue setup (${setup.doneCount} of ${SETUP_STEP_TOTAL})`}
            onClick={continueSetup}
          />
        </div>
      )}
      <div
        className="flex min-h-0 flex-1 flex-col items-center px-4 pt-10 pb-10 lg:overflow-y-auto"
        role="presentation"
        onClick={() => selectedCategory && setSelectedCategory(null)}
      >
        <div className="w-full max-w-[680px] animate-scale-in md:mt-[20vh]">
          {header}
          {liveRegion}
          {noWarehouse ? (
            <NoWarehouseHome />
          ) : (
            <>
              {composer}
              {suggestions}
            </>
          )}
        </div>
      </div>
      {showSetupPanel && (
        <SetupPanel ref={setupPanelRef} progress={setup} onTryAudience={noWarehouse ? undefined : tryAudience} />
      )}
    </div>
  );
}
